import type { User } from '@shared/types/auth';
import { keycloakConfig, authEndpoints } from './keycloak.config';

/**
 * Result of a credential step. When `otpRequired` is true the caller must
 * complete the TOTP challenge with `verifyOtp` before a session is issued.
 */
export interface AuthResult {
  user: User | null;
  otpRequired: boolean;
  /** Opaque token that ties the OTP challenge to the pending login (mocked when offline). */
  otpChallengeId?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  /** ISO 3166-1 alpha-2 country code, e.g. "US". */
  country: string;
  acceptedTerms: boolean;
}

export interface OtpEnrollment {
  /** otpauth:// URI encoded for QR rendering. */
  otpauthUri: string;
  /** Base32 secret shown as a manual-entry fallback. */
  secret: string;
}

/** Thrown for expected auth failures so the UI can show friendly copy. */
export class AuthError extends Error {
  constructor(
    message: string,
    public code:
      | 'invalid_credentials'
      | 'invalid_otp'
      | 'otp_expired'
      | 'email_exists'
      | 'terms_required'
      | 'network'
      | 'unknown' = 'unknown'
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

// ── Mock backend (used when Keycloak is not enabled) ───────────────────────

const MOCK_OTP = '123456';

function mockUser(email: string, name?: string, overrides?: Partial<User>): User {
  return {
    id: `kc_${btoa(email).replace(/=/g, '').slice(0, 12)}`,
    email,
    name: name ?? email.split('@')[0],
    fullName: name ?? email.split('@')[0],
    role: 'merchant',
    permissions: [],
    isPhoneVerified: false,
    has2FA: false,
    hasPasskey: false,
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    ...overrides,
  };
}

/** Emails ending in this suffix simulate an account with TOTP enabled. */
const MOCK_OTP_SUFFIX = '+otp';

async function postJson<T>(url: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthError('Unable to reach the authentication server.', 'network');
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { code?: AuthError['code']; message?: string };
    throw new AuthError(data.message ?? 'Authentication failed.', data.code ?? 'unknown');
  }
  return res.json() as Promise<T>;
}

/**
 * Auth seam. Every method targets the same-origin BFF when Keycloak is enabled,
 * otherwise resolves against a deterministic in-memory mock so the app works in
 * preview. Flip to live by setting VITE_KEYCLOAK_ENABLED=true and the related vars.
 */
class KeycloakService {
  get isLive() {
    return keycloakConfig.enabled;
  }

  async login({ email, password }: LoginInput): Promise<AuthResult> {
    if (this.isLive) {
      return postJson<AuthResult>(authEndpoints.login, { email, password });
    }
    // Mock: any non-empty password of length >= 6 succeeds.
    await delay();
    if (!password || password.length < 6) {
      throw new AuthError('Incorrect email or password.', 'invalid_credentials');
    }
    const wantsOtp = email.includes(MOCK_OTP_SUFFIX);
    if (wantsOtp) {
      return { user: null, otpRequired: true, otpChallengeId: `mock_${Date.now()}` };
    }
    return { user: mockUser(email), otpRequired: false };
  }

  async register(input: RegisterInput): Promise<AuthResult> {
    if (!input.acceptedTerms) {
      throw new AuthError('You must accept the Terms and Privacy Policy.', 'terms_required');
    }
    if (this.isLive) {
      return postJson<AuthResult>(authEndpoints.register, input);
    }
    await delay();
    if (input.email.startsWith('taken@')) {
      throw new AuthError('An account with this email already exists.', 'email_exists');
    }
    const fullName = `${input.firstName} ${input.lastName}`.trim();
    return { user: mockUser(input.email, fullName), otpRequired: false };
  }

  async verifyOtp(challengeId: string, code: string, email: string): Promise<User> {
    if (this.isLive) {
      return postJson<{ user: User }>(authEndpoints.otpVerify, { challengeId, code }).then((r) => r.user);
    }
    await delay();
    if (code === MOCK_OTP) {
      return mockUser(email, undefined, { has2FA: true });
    }
    throw new AuthError('That code is incorrect. Please try again.', 'invalid_otp');
  }

  /** Begin TOTP enrollment — returns an otpauth URI to render as a QR code. */
  async startOtpEnrollment(email: string): Promise<OtpEnrollment> {
    if (this.isLive) {
      return postJson<OtpEnrollment>(authEndpoints.otpEnrollStart, { email });
    }
    await delay();
    const secret = 'JBSWY3DPEHPK3PXP';
    const issuer = 'Barracuda';
    const otpauthUri = `otpauth://totp/${encodeURIComponent(`${issuer}:${email}`)}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
    return { otpauthUri, secret };
  }

  /** Confirm enrollment by verifying the first generated code. */
  async confirmOtpEnrollment(email: string, code: string): Promise<void> {
    if (this.isLive) {
      await postJson(authEndpoints.otpEnrollConfirm, { email, code });
      return;
    }
    await delay();
    if (code !== MOCK_OTP) {
      throw new AuthError('That code is incorrect. Please try again.', 'invalid_otp');
    }
  }

  async forgotPassword(email: string): Promise<void> {
    if (this.isLive) {
      await postJson(authEndpoints.forgotPassword, { email });
      return;
    }
    await delay();
    // Always succeed to avoid account enumeration.
  }

  /** Redirect the browser to the BFF, which starts the brokered IdP flow. */
  startSocial(provider: 'google' | 'passkey'): void {
    if (this.isLive) {
      window.location.href = authEndpoints.social(provider);
      return;
    }
    // Mock: no-op; callers resolve a mock session directly.
  }

  async socialMock(provider: 'google' | 'passkey'): Promise<User> {
    await delay();
    return mockUser(`${provider}-user@example.com`, provider === 'google' ? 'Google User' : 'Passkey User', {
      hasPasskey: provider === 'passkey',
    });
  }
}

function delay(ms = 500): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const keycloakService = new KeycloakService();
